import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const EMPTY_TIER = { tag: '', min_orders: 2, max_orders: null, percent: 3, cap: 100, discount_name: '' };
const MAX_PINCODES = 900;
const PINCODE_PATTERN = /^\d{6}$/;

export default function RepeatCustomerPage() {
  const { currentUser } = useAuth();

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [message, setMessage] = useState(null);

  const [prepaidConfig, setPrepaidConfig] = useState({ percent: '', cap: '', discount_name: '' });
  const [prepaidSaving, setPrepaidSaving] = useState(false);
  const [prepaidMessage, setPrepaidMessage] = useState(null);

  const [codPincodesText, setCodPincodesText] = useState('');
  const [codSaving, setCodSaving] = useState(false);
  const [codMessage, setCodMessage] = useState(null);

  const [wholesaleLadder, setWholesaleLadder] = useState({ tier2Percent: '', tier3Percent: '', tier3MinQty: '' });
  const [wholesaleSaving, setWholesaleSaving] = useState(false);
  const [wholesaleMessage, setWholesaleMessage] = useState(null);

  const [loggedInLadder, setLoggedInLadder] = useState({ tier2Percent: '', tier3Percent: '', tier3MinQty: '' });
  const [loggedInSaving, setLoggedInSaving] = useState(false);
  const [loggedInMessage, setLoggedInMessage] = useState(null);

  const [welcomeConfig, setWelcomeConfig] = useState({ percent: '', cap: '', discount_name: '' });
  const [welcomeSaving, setWelcomeSaving] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(null);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    return fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch('/api/v1/admin/repeat-customer/config');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load tier config.');
      setTiers(data.tiers || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/repeat-customer/status');
      const data = await res.json();
      if (data.success) setSyncStatus(data.status);
    } catch {
      // Non-critical — status display just stays blank if this fails.
    }
  }, [authedFetch]);

  const loadPrepaidConfig = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/prepaid-discount/config');
      const data = await res.json();
      if (data.success) setPrepaidConfig(data.config);
    } catch (err) {
      setPrepaidMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const loadCodPincodeConfig = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/cod-pincode-exclusion/config');
      const data = await res.json();
      if (data.success) setCodPincodesText((data.config.pincodes || []).join('\n'));
    } catch (err) {
      setCodMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const loadWholesaleLadder = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/wholesale-ladder/config');
      const data = await res.json();
      if (data.success) setWholesaleLadder(data.config);
    } catch (err) {
      setWholesaleMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const loadLoggedInLadder = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/logged-in-ladder/config');
      const data = await res.json();
      if (data.success) setLoggedInLadder(data.config);
    } catch (err) {
      setLoggedInMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const loadWelcomeConfig = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v1/admin/welcome-discount/config');
      const data = await res.json();
      if (data.success) setWelcomeConfig(data.config);
    } catch (err) {
      setWelcomeMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  useEffect(() => {
    loadConfig();
    loadSyncStatus();
    loadPrepaidConfig();
    loadCodPincodeConfig();
    loadWholesaleLadder();
    loadLoggedInLadder();
    loadWelcomeConfig();
  }, [loadConfig, loadSyncStatus, loadPrepaidConfig, loadCodPincodeConfig, loadWholesaleLadder, loadLoggedInLadder, loadWelcomeConfig]);

  const updateTier = (index, field, value) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const addTier = () => setTiers((prev) => [...prev, { ...EMPTY_TIER }]);
  const removeTier = (index) => setTiers((prev) => prev.filter((_, i) => i !== index));

  const saveConfig = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const normalized = tiers.map((t) => ({
        tag: String(t.tag || '').trim(),
        min_orders: Number(t.min_orders),
        max_orders: t.max_orders === '' || t.max_orders == null ? null : Number(t.max_orders),
        percent: Number(t.percent),
        cap: Number(t.cap),
        discount_name: String(t.discount_name || '').trim(),
      }));
      const res = await authedFetch('/api/v1/admin/repeat-customer/config', {
        method: 'PUT',
        body: JSON.stringify({ tiers: normalized }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setMessage({ type: 'success', text: 'Tier config saved. Click "Sync Now" to push it live.' });
      await loadConfig();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    setIsSyncing(true);
    setMessage(null);
    try {
      const res = await authedFetch('/api/v1/admin/repeat-customer/sync', { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Sync failed.');
      const { summary } = data;
      setMessage({
        type: 'success',
        text: `Synced: ${summary.tagged} tagged, ${summary.skippedWholesale} wholesale skipped, ${summary.untagged} below tier 1, ${summary.errorCount} errors (of ${summary.customersConsidered} customers considered).`,
      });
      await loadSyncStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsSyncing(false);
    }
  };

  const savePrepaidConfig = async () => {
    setPrepaidSaving(true);
    setPrepaidMessage(null);
    try {
      const normalized = {
        percent: Number(prepaidConfig.percent),
        cap: Number(prepaidConfig.cap),
        discount_name: String(prepaidConfig.discount_name || '').trim(),
      };
      const res = await authedFetch('/api/v1/admin/prepaid-discount/config', {
        method: 'PUT',
        body: JSON.stringify({ config: normalized }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setPrepaidConfig(data.config);
      setPrepaidMessage({ type: 'success', text: 'Prepaid discount config saved and pushed to Shopify.' });
    } catch (err) {
      setPrepaidMessage({ type: 'error', text: err.message });
    } finally {
      setPrepaidSaving(false);
    }
  };

  const parsedPincodes = Array.from(
    new Set(codPincodesText.split(/[\s,]+/).map((p) => p.trim()).filter(Boolean)),
  );
  const invalidPincodes = parsedPincodes.filter((p) => !PINCODE_PATTERN.test(p));

  const saveCodPincodeConfig = async () => {
    setCodSaving(true);
    setCodMessage(null);
    try {
      if (invalidPincodes.length > 0) {
        throw new Error(`Not 6-digit pincodes: ${invalidPincodes.slice(0, 5).join(', ')}${invalidPincodes.length > 5 ? '…' : ''}`);
      }
      if (parsedPincodes.length > MAX_PINCODES) {
        throw new Error(`${parsedPincodes.length} pincodes exceeds the max of ${MAX_PINCODES}.`);
      }
      const res = await authedFetch('/api/v1/admin/cod-pincode-exclusion/config', {
        method: 'PUT',
        body: JSON.stringify({ pincodes: parsedPincodes }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setCodPincodesText((data.config.pincodes || []).join('\n'));
      setCodMessage({ type: 'success', text: `Saved ${data.config.pincodes.length} pincodes and pushed to Shopify.` });
    } catch (err) {
      setCodMessage({ type: 'error', text: err.message });
    } finally {
      setCodSaving(false);
    }
  };

  const saveWholesaleLadder = async () => {
    setWholesaleSaving(true);
    setWholesaleMessage(null);
    try {
      const normalized = {
        tier2Percent: Number(wholesaleLadder.tier2Percent),
        tier3Percent: Number(wholesaleLadder.tier3Percent),
        tier3MinQty: Number(wholesaleLadder.tier3MinQty),
      };
      const res = await authedFetch('/api/v1/admin/wholesale-ladder/config', {
        method: 'PUT',
        body: JSON.stringify({ config: normalized }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setWholesaleLadder(data.config);
      setWholesaleMessage({ type: 'success', text: 'Wholesale ladder config saved and pushed to Shopify.' });
    } catch (err) {
      setWholesaleMessage({ type: 'error', text: err.message });
    } finally {
      setWholesaleSaving(false);
    }
  };

  const saveLoggedInLadder = async () => {
    setLoggedInSaving(true);
    setLoggedInMessage(null);
    try {
      const normalized = {
        tier2Percent: Number(loggedInLadder.tier2Percent),
        tier3Percent: Number(loggedInLadder.tier3Percent),
        tier3MinQty: Number(loggedInLadder.tier3MinQty),
      };
      const res = await authedFetch('/api/v1/admin/logged-in-ladder/config', {
        method: 'PUT',
        body: JSON.stringify({ config: normalized }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setLoggedInLadder(data.config);
      setLoggedInMessage({ type: 'success', text: 'Logged-in ladder config saved and pushed to Shopify.' });
    } catch (err) {
      setLoggedInMessage({ type: 'error', text: err.message });
    } finally {
      setLoggedInSaving(false);
    }
  };

  const saveWelcomeConfig = async () => {
    setWelcomeSaving(true);
    setWelcomeMessage(null);
    try {
      const normalized = {
        percent: Number(welcomeConfig.percent),
        cap: Number(welcomeConfig.cap),
        discount_name: String(welcomeConfig.discount_name || '').trim(),
      };
      const res = await authedFetch('/api/v1/admin/welcome-discount/config', {
        method: 'PUT',
        body: JSON.stringify({ config: normalized }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setWelcomeConfig(data.config);
      setWelcomeMessage({ type: 'success', text: 'Welcome discount config saved and pushed to Shopify.' });
    } catch (err) {
      setWelcomeMessage({ type: 'error', text: err.message });
    } finally {
      setWelcomeSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading repeat-customer tiers...</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <h1>Repeat Customer Discount</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        Non-wholesale customers get a percent-off-order discount (capped at a max amount) once they
        cross a delivered-order threshold — cancelled/returned orders don't count. A nightly job
        (00:25 IST) tags eligible customers on Shopify and keeps this config in sync with the
        fabric-quantity-tiers app's discount Function; use "Sync Now" to push a change immediately
        instead of waiting for the schedule.
      </p>

      {message && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: message.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${message.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: message.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {message.text}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 14 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 6 }}>Tag</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Min delivered orders</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Max delivered orders</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Percent off</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Capped at (₹)</th>
            <th style={{ textAlign: 'left', padding: 6 }}>Discount name (shown to customer)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier, index) => (
            <tr key={index} style={{ borderTop: '1px solid #eee' }}>
              <td style={{ padding: 6 }}>
                <input value={tier.tag} onChange={(e) => updateTier(index, 'tag', e.target.value)} placeholder="repeat-2to5" style={{ width: 130 }} />
              </td>
              <td style={{ padding: 6 }}>
                <input type="number" min="1" value={tier.min_orders} onChange={(e) => updateTier(index, 'min_orders', e.target.value)} style={{ width: 90 }} />
              </td>
              <td style={{ padding: 6 }}>
                <input
                  type="number"
                  min="1"
                  value={tier.max_orders ?? ''}
                  onChange={(e) => updateTier(index, 'max_orders', e.target.value)}
                  placeholder="no limit"
                  style={{ width: 90 }}
                />
              </td>
              <td style={{ padding: 6 }}>
                <input type="number" min="0" max="100" value={tier.percent} onChange={(e) => updateTier(index, 'percent', e.target.value)} style={{ width: 70 }} />
              </td>
              <td style={{ padding: 6 }}>
                <input type="number" min="0" value={tier.cap} onChange={(e) => updateTier(index, 'cap', e.target.value)} style={{ width: 90 }} />
              </td>
              <td style={{ padding: 6 }}>
                <input value={tier.discount_name} onChange={(e) => updateTier(index, 'discount_name', e.target.value)} placeholder="Loyal Customer Reward" style={{ width: 220 }} />
              </td>
              <td style={{ padding: 6 }}>
                <button type="button" onClick={() => removeTier(index)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={addTier} style={{ marginTop: 10 }}>+ Add tier</button>

      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" onClick={saveConfig} disabled={saving}>{saving ? 'Saving…' : 'Save Tiers'}</button>
        <button type="button" onClick={runSync} disabled={isSyncing}>{isSyncing ? 'Syncing…' : 'Sync Now'}</button>
        {syncStatus?.lastRunAt && (
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            Last run: {new Date(syncStatus.lastRunAt).toLocaleString()} — {syncStatus.lastTagged} tagged, {syncStatus.lastErrorCount} errors
          </span>
        )}
      </div>

      <p style={{ marginTop: 16, color: '#6b7280', fontSize: 13, maxWidth: 640 }}>
        Note: adding a brand-new tag here (or renaming an existing one) needs a small Function
        code change and redeploy in the fabric-quantity-tiers app before it'll actually apply at
        checkout — the Function has to know the exact tag strings in advance. Editing an existing
        tier's percent, cap, or discount name does <em>not</em> need a redeploy, just Save + Sync.
      </p>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

      <h1>Prepaid Discount</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        A separate percent-off-order discount (capped at a max amount), available to anyone who
        applies the code — not tied to order history or customer tags. Runs as its own, separate
        Shopify Function extension (deliberately not sharing code with the repeat-customer/ladder/
        BXGY one), so it can be combined with those at checkout without any risk of double-counting.
        Saving here writes straight to Shopify — no separate sync step needed.
      </p>

      {prepaidMessage && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: prepaidMessage.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${prepaidMessage.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: prepaidMessage.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {prepaidMessage.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Percent off
          <input
            type="number"
            min="0"
            max="100"
            value={prepaidConfig.percent}
            onChange={(e) => setPrepaidConfig((prev) => ({ ...prev, percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Capped at (₹)
          <input
            type="number"
            min="0"
            value={prepaidConfig.cap}
            onChange={(e) => setPrepaidConfig((prev) => ({ ...prev, cap: e.target.value }))}
            style={{ width: 110 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Discount name (shown to customer)
          <input
            value={prepaidConfig.discount_name}
            onChange={(e) => setPrepaidConfig((prev) => ({ ...prev, discount_name: e.target.value }))}
            placeholder="Prepaid Discount"
            style={{ width: 260 }}
          />
        </label>
        <button type="button" onClick={savePrepaidConfig} disabled={prepaidSaving}>
          {prepaidSaving ? 'Saving…' : 'Save & Push to Shopify'}
        </button>
      </div>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

      <h1>COD Pincode Exclusion</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        Hides Cash on Delivery at checkout for shipping addresses in these pincodes — one more
        independent trigger alongside the Prepaid discount and Custom Fabric Print, all handled by
        the same payment-restrictions Function. One pincode per line (or comma/space separated),
        6 digits each. Capped at {MAX_PINCODES} entries — a Shopify Functions platform limit on how
        large this metafield can be, not an arbitrary choice.
      </p>

      {codMessage && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: codMessage.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${codMessage.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: codMessage.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {codMessage.text}
        </div>
      )}

      <textarea
        value={codPincodesText}
        onChange={(e) => setCodPincodesText(e.target.value)}
        placeholder={'110001\n400001\n560001'}
        rows={10}
        style={{ width: '100%', maxWidth: 400, fontFamily: 'monospace', fontSize: 13 }}
      />

      <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" onClick={saveCodPincodeConfig} disabled={codSaving}>
          {codSaving ? 'Saving…' : 'Save & Push to Shopify'}
        </button>
        <span
          style={{
            fontSize: 13,
            color: parsedPincodes.length > MAX_PINCODES || invalidPincodes.length > 0 ? '#991b1b' : '#6b7280',
          }}
        >
          {parsedPincodes.length} / {MAX_PINCODES} pincodes
          {invalidPincodes.length > 0 && ` — ${invalidPincodes.length} invalid`}
        </span>
      </div>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

      <h1>Wholesale Ladder</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        Fixed quantity-ladder percentages applied to wholesale customers only — completely separate
        from the retail ladder config above, so retail promos (including the Discount Calendar)
        never change wholesale pricing. Update this only when the wholesale rate itself changes.
        2 meters gets tier 2's percent; reaching the minimum quantity below gets tier 3's percent
        instead.
      </p>

      {wholesaleMessage && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: wholesaleMessage.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${wholesaleMessage.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: wholesaleMessage.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {wholesaleMessage.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 2 percent off (2 meters)
          <input
            type="number"
            min="0"
            max="100"
            value={wholesaleLadder.tier2Percent}
            onChange={(e) => setWholesaleLadder((prev) => ({ ...prev, tier2Percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 3 percent off
          <input
            type="number"
            min="0"
            max="100"
            value={wholesaleLadder.tier3Percent}
            onChange={(e) => setWholesaleLadder((prev) => ({ ...prev, tier3Percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 3 minimum meters
          <input
            type="number"
            min="2"
            value={wholesaleLadder.tier3MinQty}
            onChange={(e) => setWholesaleLadder((prev) => ({ ...prev, tier3MinQty: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <button type="button" onClick={saveWholesaleLadder} disabled={wholesaleSaving}>
          {wholesaleSaving ? 'Saving…' : 'Save & Push to Shopify'}
        </button>
      </div>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

      <h1>Logged-In Customer Ladder</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        Better quantity-ladder percentages for any authenticated (logged-in) customer — better than
        the guest/retail default above, but overridden by the Wholesale Ladder if the account is
        also wholesale. The incentive to log in doubles as first-party contact capture for ad
        retargeting. Same rule as the other ladders: 2 meters gets tier 2's percent, reaching the
        minimum quantity below gets tier 3's percent instead.
      </p>

      {loggedInMessage && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: loggedInMessage.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${loggedInMessage.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: loggedInMessage.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {loggedInMessage.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 2 percent off (2 meters)
          <input
            type="number"
            min="0"
            max="100"
            value={loggedInLadder.tier2Percent}
            onChange={(e) => setLoggedInLadder((prev) => ({ ...prev, tier2Percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 3 percent off
          <input
            type="number"
            min="0"
            max="100"
            value={loggedInLadder.tier3Percent}
            onChange={(e) => setLoggedInLadder((prev) => ({ ...prev, tier3Percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Tier 3 minimum meters
          <input
            type="number"
            min="2"
            value={loggedInLadder.tier3MinQty}
            onChange={(e) => setLoggedInLadder((prev) => ({ ...prev, tier3MinQty: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <button type="button" onClick={saveLoggedInLadder} disabled={loggedInSaving}>
          {loggedInSaving ? 'Saving…' : 'Save & Push to Shopify'}
        </button>
      </div>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

      <h1>Welcome (First-Order) Discount</h1>
      <p style={{ color: '#6b7280', maxWidth: 640 }}>
        A percent-off-order discount (capped at a max amount) for customers on their very first
        order — checked via Shopify's own order count for the account, so the customer must be
        logged in for this to apply (a guest checkout has no order history to check). Open to
        wholesale accounts too, not just retail. Runs as its own, separate Shopify Function
        extension (deliberately not sharing code with any of the others), so it can combine with
        LOYALTY, PREPAY, and the ladder discounts without any risk of double-counting. Saving here
        writes straight to Shopify — no separate sync step needed.
      </p>

      {welcomeMessage && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 10, fontSize: 14,
          background: welcomeMessage.type === 'error' ? '#fef2f2' : '#f0fff4',
          border: `1px solid ${welcomeMessage.type === 'error' ? '#fecaca' : '#c6f6d5'}`,
          color: welcomeMessage.type === 'error' ? '#991b1b' : '#276749',
        }}>
          {welcomeMessage.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Percent off
          <input
            type="number"
            min="0"
            max="100"
            value={welcomeConfig.percent}
            onChange={(e) => setWelcomeConfig((prev) => ({ ...prev, percent: e.target.value }))}
            style={{ width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Capped at (₹)
          <input
            type="number"
            min="0"
            value={welcomeConfig.cap}
            onChange={(e) => setWelcomeConfig((prev) => ({ ...prev, cap: e.target.value }))}
            style={{ width: 110 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Discount name (shown to customer)
          <input
            value={welcomeConfig.discount_name}
            onChange={(e) => setWelcomeConfig((prev) => ({ ...prev, discount_name: e.target.value }))}
            placeholder="Welcome Discount"
            style={{ width: 260 }}
          />
        </label>
        <button type="button" onClick={saveWelcomeConfig} disabled={welcomeSaving}>
          {welcomeSaving ? 'Saving…' : 'Save & Push to Shopify'}
        </button>
      </div>
    </div>
  );
}
